import React, { useState, useEffect } from "react";
import InputField from "components/fields/InputField";
import { Link, useNavigate, useLocation } from "react-router-dom";
import LogoIcon from "components/LogoIcon/LogoIcon";
import { registerUser } from "services/apiService";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface FormData {
  fullname: string;
  email: string;
  password: string;
  confirmPassword: string;
  organization: string;
}

export default function Register() {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState<FormData>({
    fullname: '',
    email: '',
    password: '',
    confirmPassword: '',
    organization: '',
  });

  const [isOrgDisabled, setIsOrgDisabled] = useState(false);
  const [isEmailDisabled, setIsEmailDisabled] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const organization = params.get('organization');
    const email = params.get('email');

    // Block direct URL access — only reachable via invite link
    if (!organization) {
      navigate('/signin/', { replace: true });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      ...(organization ? { organization } : {}),
      ...(email ? { email } : {}),
    }));
    if (organization) setIsOrgDisabled(true);
    if (email) setIsEmailDisabled(true);
  }, [location, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    if (!formData.fullname || !formData.email || !formData.password || !formData.organization) {
      toast.error('Please fill in all required fields.');
      return;
    }

    try {
      await registerUser({
        fullname: formData.fullname,
        email: formData.email,
        password: formData.password,
        organization: formData.organization,
      });
      toast.success('Account created! Redirecting to sign in…');
      setTimeout(() => navigate('/signin/'), 2000);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Registration failed. Please try again.');
    }
  };

  return (
    <div className="flex h-screen bg-white dark:bg-darkPrimary">
      {/* Left: form */}
      <div className="flex flex-1 items-center justify-center px-6 overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <h4 className="mb-2 text-3xl font-bold text-navy-700 dark:text-white">
            Create Your Account
          </h4>
          <p className="mb-6 text-base text-[#374151] dark:text-gray-300">
            You've been invited — complete your details to get started.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <InputField
              variant="auth"
              extra=""
              label="Full Name*"
              placeholder="John Doe"
              id="fullname"
              type="text"
              value={formData.fullname}
              onChange={handleChange}
            />
            <InputField
              variant="auth"
              extra=""
              label="Email*"
              placeholder="mail@example.com"
              id="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              disabled={isEmailDisabled}
            />
            <InputField
              variant="auth"
              extra=""
              label="Organisation*"
              placeholder="My Organisation"
              id="organization"
              type="text"
              value={formData.organization}
              onChange={handleChange}
              disabled={isOrgDisabled}
            />
            <InputField
              variant="auth"
              extra=""
              label="Password*"
              placeholder="Create a password"
              id="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
            />
            <InputField
              variant="auth"
              extra=""
              label="Confirm Password*"
              placeholder="Re-enter your password"
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
            />

            <button
              type="submit"
              className="w-full rounded-xl bg-brand-500 py-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700"
            >
              Create Account
            </button>
          </form>

          <div className="mt-4 text-center">
            <span className="text-sm text-navy-700 dark:text-gray-600">
              Already have an account?
            </span>
            <Link
              to="/signin/"
              className="ml-1 text-sm font-medium text-brand-500 hover:text-brand-600"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>

      {/* Right: logo panel — matches sign-in page */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-[#EDFDFB]">
        <LogoIcon />
      </div>

      <ToastContainer />
    </div>
  );
}
